/*!
 * MidiStreamNotes — which notes a MIDI file actually plays.
 *
 * The one door every tool goes through to READ a .mid with @tonejs/midi
 * (/vendor/midi/Midi.min.js): MidiStreamNotes.parse(Midi, bytes) instead of
 * new Midi(bytes).
 *
 * Why the door exists. @tonejs/midi pairs a note-on with "the first note-off of
 * the same pitch whose time is ≥ the note-on's time", inside tracks it has
 * split by (program, channel). That rule ignores three things the file says:
 *   • ORDER inside a tick. A note-off written BEFORE a note-on at the same tick
 *     cannot end that note — the note did not exist yet. Chord generators write
 *     exactly this at every chord change ("release whatever the previous chord
 *     held, then strike the new one"), including releases for notes that were
 *     never struck. @tonejs/midi hands such a release to the new note, and the
 *     note comes out ZERO ticks long.
 *   • A PROGRAM CHANGE while a note sounds. The release lands in the next
 *     split track, and the strike is dropped for want of a partner (a whole
 *     drum kit in the corpus read as 0 of 563 hits).
 *   • ALL NOTES OFF. Notes a file ends with CC 123 instead of note-offs pair
 *     with some later release of the same key, or with none.
 * Measured on the file that reported it (feedback 2026-09-23, /midi-humanizer,
 * "midi cutting out in chorus"): 221 of 2 564 notes parsed as zero length,
 * clustered on the first two bars of every section — 27 of 31 notes of one
 * bar, the whole band for two bars at another. A sequencer plays the file
 * correctly; every tool of ours played it with holes.
 *
 * What this module does instead is what a sequencer's import does: walk each
 * track's events in STREAM order, keep the sounding strikes per (channel, key)
 * oldest first, and
 *   note-on (vel > 0)          start a note
 *   note-off / note-on vel 0   end the oldest sounding strike of that key;
 *                              nothing sounding → the release is ignored
 *   CC 120, CC 123..127        end every sounding note on that channel (All
 *                              Sound Off, All Notes Off and the mode messages
 *                              the spec says imply it)
 * Measured against @tonejs/midi on the 17 256 files of the Lakh "clean_midi"
 * corpus: the reading differs in 1 373 files (8 %), and an independent
 * implementation of the same rule (mido, Python) agrees with this one note
 * for note on 297 of 298 sampled files (the 298th is a damaged file mido reads
 * differently from both). A synth-style "one note per key" rule was measured
 * too and rejected: it changed 57 % of the corpus, mostly by merging unisons
 * the author doubled on purpose — an interpretation, not a repair.
 * Notes still sounding at the end of the track are dropped, as @tonejs/midi
 * and pretty_midi do: a missing release is a damaged file, and holding the
 * note to the end of the song would turn one lost byte into a drone.
 *
 * Only tracks whose notes DIFFER from @tonejs/midi's are rebuilt, so a
 * well-formed file comes back as the very same object graph — and a tool that
 * writes the file back gives the same bytes it gave before this door existed.
 *
 * Classic script with no imports/exports: runs as a page <script>, as a side-
 * effect ES-module import (globalThis), in a worker and in Node (module.exports).
 */
(function (root) {
    'use strict';

    /* ── raw SMF reader: per-track notes in stream order ───────────────────── */

    function u32(b, o) { return ((b[o] << 24) >>> 0) + (b[o + 1] << 16) + (b[o + 2] << 8) + b[o + 3]; }
    function u16(b, o) { return (b[o] << 8) + b[o + 1]; }
    function tag(b, o) { return String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]); }

    function toBytes(input) {
        if (input instanceof Uint8Array) return input;
        if (input instanceof ArrayBuffer) return new Uint8Array(input);
        if (input && input.buffer instanceof ArrayBuffer && typeof input.byteLength === 'number') {
            return new Uint8Array(input.buffer, input.byteOffset || 0, input.byteLength);
        }
        if (Array.isArray(input)) return Uint8Array.from(input);
        return null;
    }

    /**
     * Notes per TRACK AS @tonejs/midi LAYS THEM OUT. The library does not keep
     * the file's tracks: it splits each one by (current program, channel), in
     * order of first appearance, so a one-track format-0 file comes back as a
     * track per instrument. The same split is reproduced here, so track i of
     * the result is track i of the library's — except that a note is paired
     * across the split. (@tonejs/midi pairs INSIDE a split track: a program
     * change while a note sounds moves its note-off into the next split track,
     * and the note is lost.)
     *
     * Tempo changes come back too, in file order (every track), for a caller
     * that turns ticks into time without @tonejs/midi (TimbricaMidiParser), and
     * so do time signatures, for a caller that cuts the notes into bars
     * (/music-sequencer's MIDI import: a 3/4 file cut into 4/4 bars puts every
     * downbeat on the wrong step).
     *
     * @returns {null | {format:number, ppq:number, tempos:Array<{ticks:number,
     *           usPerQuarter:number}>, timeSignatures:Array<{ticks:number, num:number,
     *           den:number}>, tracks:Array<{notes:Array, orphanOffs:number,
     *           stacked:number, hanging:number, allOff:number, zeroLength:number}>}}
     *          null when the bytes are not a plain SMF this reader can walk —
     *          the caller then keeps @tonejs/midi's reading untouched.
     */
    function read(input) {
        var b = toBytes(input);
        if (!b || b.length < 14 || tag(b, 0) !== 'MThd') return null;
        var hdrLen = u32(b, 4);
        if (hdrLen < 6) return null;
        var format = u16(b, 8), nTracks = u16(b, 10), division = u16(b, 12);
        var off = 8 + hdrLen;
        var tracks = [], tempos = [], meters = [];
        for (var t = 0; t < nTracks && off + 8 <= b.length; t++) {
            if (tag(b, off) !== 'MTrk') return null;      /* @tonejs/midi throws here too */
            var len = u32(b, off + 4);
            var start = off + 8;
            var subs = walkTrack(b, start, Math.min(b.length, start + len), tempos, meters);
            if (!subs) return null;
            for (var i = 0; i < subs.length; i++) tracks.push(subs[i]);
            off = start + len;
        }
        tempos.sort(function (x, y) { return x.ticks - y.ticks; });   /* stable: file order on a tie */
        meters.sort(function (x, y) { return x.ticks - y.ticks; });
        return { format: format, ppq: (division & 0x8000) ? 0 : division, tempos: tempos, timeSignatures: meters, tracks: tracks };
    }

    /* Variable-length quantity, read EXACTLY as midi-file (inside @tonejs/midi)
       reads it: no length limit, 32-bit wrap included. Damaged files carry
       five-byte delta times (measured: Tune 1000 karaoke files in the Lakh
       corpus), and a reader that stops there — as a spec-strict one would —
       disagrees with the library about every event after it. This module must
       see the same events and differ in one thing only: which note-off ends
       which note. The wrap can make a delta NEGATIVE (the library then steps
       back in time, and so do we), so the end of the track mid-number is
       signalled with null, not with a negative sentinel. */
    function vlq(b, s) {
        var v = 0;
        while (s.p < s.end) {
            var c = b[s.p++];
            if (c & 0x80) { v += (c & 0x7f); v <<= 7; }
            else return v + c;
        }
        return null;
    }

    function newSub() {
        return { notes: [], orphanOffs: 0, stacked: 0, hanging: 0, allOff: 0, zeroLength: 0 };
    }

    /** One file track → the split tracks @tonejs/midi makes of it (0 for an empty chunk). */
    function walkTrack(b, p, end, tempos, meters) {
        var s = { p: p, end: end };
        var tick = 0, status = 0, order = 0;
        var open = {};                  /* "channel key" → sounding strikes, oldest first */
        var subs = [newSub()];          /* split 0 also holds everything without a channel */
        var splitOf = {};               /* "program channel" → split index */
        var program = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        var keys = 0, events = 0;

        function splitFor(ch) {
            var id = program[ch] + ' ' + ch;
            if (!(id in splitOf)) {
                splitOf[id] = keys++;
                if (!subs[splitOf[id]]) subs.push(newSub());
            }
            return splitOf[id];
        }
        function close(q, ch, key, offVel) {
            var on = q.shift();
            var home = subs[on.sub];
            if (tick === on.tick) home.zeroLength++;
            home.notes.push({ channel: ch, midi: key, ticks: on.tick, durationTicks: tick - on.tick,
                              velocity: on.vel, offVelocity: offVel, order: on.order });
        }
        function strike(ch, key, vel, sub) {
            var k = ch + ' ' + key;
            var q = open[k] || (open[k] = []);
            if (q.length) subs[sub].stacked++;
            q.push({ tick: tick, vel: vel, order: order++, sub: sub, ch: ch, key: key });
        }
        function release(ch, key, offVel, sub) {
            var q = open[ch + ' ' + key];
            if (!q || !q.length) { subs[sub].orphanOffs++; return; }
            close(q, ch, key, offVel);
        }
        function releaseChannel(ch) {
            Object.keys(open).forEach(function (k) {
                var q = open[k];
                while (q.length && q[0].ch === ch) { subs[q[0].sub].allOff++; close(q, ch, q[0].key, 0); }
            });
        }

        while (s.p < s.end) {
            var d = vlq(b, s);
            if (d === null) break;                        /* truncated track: keep what was read */
            tick += d;
            if (s.p >= s.end) break;

            var st = b[s.p];
            if (st & 0x80) s.p++;
            else if (status) st = status;                 /* running status */
            else return null;
            events++;

            /* Meta and sysex do not end the walk: midi-file (inside
               @tonejs/midi) reads a track to the end of its chunk, events after
               an early end-of-track included, and so must we. */
            if (st === 0xff) {
                var type = b[s.p++];
                var ml = vlq(b, s);
                if (ml === null) break;
                if (ml < 0) return null;                  /* a length that points backwards: not ours to guess */
                if (type === 0x51 && ml === 3 && s.p + 3 <= s.end) {
                    tempos.push({ ticks: tick, usPerQuarter: (b[s.p] << 16) + (b[s.p + 1] << 8) + b[s.p + 2] });
                }
                if (type === 0x58 && ml >= 2 && s.p + 2 <= s.end && meters && b[s.p] > 0 && b[s.p + 1] < 8) {
                    meters.push({ ticks: tick, num: b[s.p], den: 1 << b[s.p + 1] });
                }
                s.p += ml;
                continue;
            }
            if (st === 0xf0 || st === 0xf7) {
                var sl = vlq(b, s);
                if (sl === null) break;
                if (sl < 0) return null;
                s.p += sl;
                continue;
            }
            if (st > 0xf0) return null;                   /* @tonejs/midi throws on these */
            status = st;                                  /* kept across meta/sysex, as midi-file does */

            var hi = st & 0xf0, ch = st & 0x0f;
            var n1 = hi === 0xc0 || hi === 0xd0 ? 1 : 2;
            if (s.p + n1 > s.end) break;
            if (hi === 0xc0) program[ch] = b[s.p];        /* the change itself opens the new split */
            var sub = splitFor(ch);

            if (hi === 0x80 || hi === 0x90) {
                /* data bytes unmasked, as midi-file reads them */
                var key = b[s.p++], vel = b[s.p++];
                if (hi === 0x90 && vel > 0) strike(ch, key, vel, sub);
                else release(ch, key, hi === 0x80 ? vel : 0, sub);
            } else if (hi === 0xb0) {
                var cc = b[s.p++]; s.p++;
                if (cc === 120 || cc >= 123) releaseChannel(ch);
            } else {
                s.p += n1;
            }
        }

        Object.keys(open).forEach(function (k2) {
            open[k2].forEach(function (on) { subs[on.sub].hanging++; });
        });
        /* note-on order — the order @tonejs/midi adds notes in */
        subs.forEach(function (sb) { sb.notes.sort(function (a, c2) { return a.order - c2.order; }); });
        /* an empty chunk becomes no track at all in @tonejs/midi */
        return events ? subs : [];
    }

    /* ── the door ──────────────────────────────────────────────────────────── */

    function signature(list) {
        var keys = new Array(list.length);
        for (var i = 0; i < list.length; i++) {
            var n = list[i];
            keys[i] = n.ticks + ':' + n.midi + ':' + n.durationTicks;
        }
        return keys.sort().join('|');
    }

    /**
     * Put stream-order notes into an already-constructed @tonejs/midi object.
     * @returns {{tracksRebuilt:number, notesBefore:number, notesAfter:number,
     *            zeroBefore:number, zeroAfter:number, orphanOffs:number, skipped?:string}}
     */
    function repair(midi, input) {
        var r = { tracksRebuilt: 0, notesBefore: 0, notesAfter: 0, zeroBefore: 0, zeroAfter: 0, orphanOffs: 0 };
        if (!midi || !midi.tracks) { r.skipped = 'no_midi'; return r; }
        midi.tracks.forEach(function (t) {
            r.notesBefore += t.notes.length;
            t.notes.forEach(function (n) { if (n.durationTicks === 0) r.zeroBefore++; });
        });
        r.notesAfter = r.notesBefore;
        r.zeroAfter = r.zeroBefore;

        var raw;
        try { raw = read(input); } catch (e) { raw = null; }
        if (!raw) { r.skipped = 'unreadable'; return r; }

        /* @tonejs/midi drops the first track of a format-1 file when its own
           pairing leaves it without duration (the conductor track). */
        var shift = raw.tracks.length - midi.tracks.length;
        if (shift !== 0 && shift !== 1) { r.skipped = 'track_map'; return r; }

        midi.tracks.forEach(function (track, i) {
            var src = raw.tracks[i + shift];
            r.orphanOffs += src.orphanOffs;
            if (signature(src.notes) === signature(track.notes)) return;
            r.notesAfter -= track.notes.length;
            track.notes.forEach(function (n) { if (n.durationTicks === 0) r.zeroAfter--; });
            track.notes.length = 0;
            src.notes.forEach(function (n) {
                track.addNote({
                    midi: n.midi,
                    ticks: n.ticks,
                    durationTicks: n.durationTicks,
                    velocity: n.velocity / 127,
                    noteOffVelocity: n.offVelocity / 127,
                });
                if (n.durationTicks === 0) r.zeroAfter++;
            });
            r.notesAfter += src.notes.length;
            r.tracksRebuilt++;
        });
        return r;
    }

    /**
     * new Midi(bytes), read the way a sequencer reads it. The repair report
     * rides along as the non-enumerable `midi.streamNotes`, for telemetry.
     */
    function parse(MidiCtor, input) {
        var midi = new MidiCtor(input);
        var report = repair(midi, input);
        try {
            Object.defineProperty(midi, 'streamNotes', { value: report, enumerable: false, configurable: true });
        } catch (e) { /* the notes are fixed either way; only the report is lost */ }
        return midi;
    }

    var api = { read: read, repair: repair, parse: parse };
    root.MidiStreamNotes = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof globalThis !== 'undefined' ? globalThis : (typeof self !== 'undefined' ? self : this));
